import { useEffect, useMemo, useRef } from 'react'

import { useDecision } from '@optimizely/react-sdk'
import type { PurchasedData } from '@space-shell/common/services/product'
import { useAuthStore } from '@space-shell/core/store'
import { lazyRemoteModule } from '@space-shell/lib/runtime'
import { useTranslation } from 'react-i18next'

type PurchaseChargeAlertProps = {
  container: Element | DocumentFragment | null
  appOrigin: string
  productId?: string
  purchases?: PurchasedData[]
}

export const PurchaseChargeAlert = ({
  container,
  appOrigin,
  productId = '',
  purchases = []
}: PurchaseChargeAlertProps) => {
  const { i18n } = useTranslation()
  const isRender = useRef(false)
  const [shouldRenderPurchaseChargeAlert] = useDecision('should_render_purchase_charge_alert')
  const { email, id, token, ucode } = useAuthStore()

  const purchaseChargeAlertpProps = useMemo(
    () => ({
      appOrigin,
      purchases,
      productId,
      user: {
        email,
        id: Number(id),
        language: i18n.language,
        token,
        ucode
      }
    }),
    [appOrigin, email, i18n.language, id, productId, purchases, token, ucode]
  )

  useEffect(() => {
    async function importModule() {
      const remoteModule = await lazyRemoteModule<(args: object) => Promise<void>>(
        'collections/renderPurchaseChargeAlert'
      )

      const shouldRender =
        shouldRenderPurchaseChargeAlert.enabled &&
        !isRender.current &&
        container &&
        purchases.length &&
        remoteModule

      if (shouldRender) {
        isRender.current = true

        remoteModule({
          container,
          props: purchaseChargeAlertpProps
        }).catch(console.error)
      }
    }

    importModule()
  }, [
    container,
    purchaseChargeAlertpProps,
    purchases.length,
    shouldRenderPurchaseChargeAlert.enabled
  ])

  return null
}

export default PurchaseChargeAlert
